import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ProtectedRoute, AdminRoute, LeadershipRoute, AdminBootstrapRoute } from '@connor-music/shared';
import { EmployeeLayout } from '@/components/EmployeeLayout';
import { AdminBootstrapLayout } from '@/components/AdminBootstrapLayout';
import Dashboard from '@/pages/Dashboard';
import Projects from '@/pages/Projects';
import ProjectDetail from '@/pages/ProjectDetail';
import Login from '@/pages/Login';
import AuthCallback from '@/pages/AuthCallback';
import Logout from '@/pages/Logout';
import AdminSetup from '@/pages/AdminSetup';
import AdminChangePassword from '@/pages/AdminChangePassword';
import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import AdminIdentity from '@/pages/admin/AdminIdentity';
import AdminConfiguration from '@/pages/admin/AdminConfiguration';
import AdminIntegrations from '@/pages/admin/AdminIntegrations';
import AdminStorage from '@/pages/admin/AdminStorage';
import AdminSecurity from '@/pages/admin/AdminSecurity';
import LeadershipSettings from '@/pages/admin/LeadershipSettings';
import CustomerNotifications from '@/pages/admin/CustomerNotifications';

function App() {
  const { user, isAuthenticated, isLoading } = useAuth();

  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/logout" element={<Logout />} />

      {/* Admin bootstrap routes (before SSO is configured) */}
      <Route path="/admin-setup" element={<AdminSetup />} />
      <Route path="/admin-setup/change-password" element={<AdminChangePassword />} />
      <Route
        path="/admin-setup/dashboard"
        element={
          <AdminBootstrapRoute>
            <AdminBootstrapLayout />
          </AdminBootstrapRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="identity" element={<AdminIdentity />} />
        <Route path="configuration" element={<AdminConfiguration />} />
        <Route path="users" element={<AdminUsers />} />
      </Route>

      {/* Protected routes (require SSO auth) */}
      <Route
        element={
          <ProtectedRoute isAuthenticated={isAuthenticated} isLoading={isLoading}>
            <EmployeeLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<Dashboard />} />
        <Route path="projects" element={<Projects />} />
        <Route path="projects/:id" element={<ProjectDetail />} />

        {/* Admin routes */}
        <Route
          path="admin"
          element={
            <AdminRoute user={user} isLoading={isLoading}>
              <Navigate to="/admin/identity" replace />
            </AdminRoute>
          }
        />
        <Route
          path="admin/users"
          element={
            <LeadershipRoute user={user} isLoading={isLoading}>
              <AdminUsers />
            </LeadershipRoute>
          }
        />
        <Route
          path="admin/identity"
          element={
            <AdminRoute user={user} isLoading={isLoading}>
              <AdminIdentity />
            </AdminRoute>
          }
        />
        <Route
          path="admin/configuration"
          element={
            <AdminRoute user={user} isLoading={isLoading}>
              <AdminConfiguration />
            </AdminRoute>
          }
        />
        <Route
          path="admin/integrations"
          element={
            <AdminRoute user={user} isLoading={isLoading}>
              <AdminIntegrations />
            </AdminRoute>
          }
        />
        {/* Leadership routes (admin + leadership roles) */}
        <Route
          path="storage"
          element={
            <LeadershipRoute user={user} isLoading={isLoading}>
              <AdminStorage />
            </LeadershipRoute>
          }
        />
        <Route
          path="security"
          element={
            <LeadershipRoute user={user} isLoading={isLoading}>
              <AdminSecurity />
            </LeadershipRoute>
          }
        />
        <Route
          path="notifications"
          element={
            <LeadershipRoute user={user} isLoading={isLoading}>
              <CustomerNotifications />
            </LeadershipRoute>
          }
        />
        <Route
          path="settings"
          element={
            <LeadershipRoute user={user} isLoading={isLoading}>
              <LeadershipSettings />
            </LeadershipRoute>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
